import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useLoginFunc } from "./login.func";

const LoginPage = () => {
  const { handlePasswordSignin } = useLoginFunc();

  const [form, setForm] = useState({
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    setApiError("");
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.username.trim()) {
      nextErrors.username = "Username is required";
    } else if (form.username.trim().length < 3) {
      nextErrors.username = "Username must be at least 3 characters";
    }

    if (!form.password.trim()) {
      nextErrors.password = "Password is required";
    } else if (form.password.length < 6) {
      nextErrors.password = "Password must be at least 6 characters";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setApiError("");

      await handlePasswordSignin({
        username: form.username.trim(),
        password: form.password,
      });
    } catch (error) {
      setApiError(
        error?.response?.data?.detail ||
          error?.message ||
          "Login failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

 return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
        background:
          "linear-gradient(135deg, #f8f9fc 0%, #f3f4f6 60%, #fff5f5 100%)",
      }}
    >
      {/* LEFT SIDE */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 3, sm: 6, md: 8 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Box sx={{ maxWidth: 560 }}>
          {/* Headline */}
          <Typography
            sx={{
              fontSize: { xs: "2.4rem", sm: "3.2rem", md: "3.8rem" },
              lineHeight: 1,
              fontWeight: 800,
              color: "#111",
              letterSpacing: "-0.04em",
              mb: 2,
            }}
          >
            Your inventory is leaking cash.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.2rem" },
              color: "rgba(20,20,20,0.7)",
              lineHeight: 1.6,
              mb: 4,
              maxWidth: 460,
            }}
          >
            Find dead stock, stop bad buying decisions, and recover blocked
            money — before it’s too late.
          </Typography>

          {/* Key outcomes */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[
              "See which products are not selling",
              "Know what to stop buying immediately",
              "Identify stock that will go dead soon",
            ].map((text) => (
              <Typography
                key={text}
                sx={{
                  fontSize: "0.95rem",
                  color: "rgba(17,24,39,0.75)",
                }}
              >
                • {text}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {/* RIGHT SIDE (LOGIN) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 4, md: 8 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: { xs: 3, sm: 4.5 },
            borderRadius: 4,
            background: "#FFFFFF",
            border: "1px solid #E8E6E1",
            boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          }}
        >
          {/* Title */}
          <Typography
            sx={{
              fontSize: "1.8rem",
              fontWeight: 700,
              color: "#111827",
              mb: 1,
            }}
          >
            Sign in
          </Typography>

          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "rgba(17,24,39,0.6)",
              mb: 3,
            }}
          >
            Upload your data → get insights in seconds
          </Typography>

          {/* Subtle tension */}
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "#B42318",
              mb: 2,
            }}
          >
            Most stores discover hidden losses after their first upload
          </Typography>

          {apiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {apiError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              value={form.username}
              onChange={handleChange("username")}
              error={Boolean(errors.username)}
              helperText={errors.username}
              margin="normal"
              autoComplete="username"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              error={Boolean(errors.password)}
              helperText={errors.password}
              margin="normal"
              autoComplete="current-password"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.3,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 700,
                background: "#111",
                "&:hover": {
                  background: "#000",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Get Insights"
              )}
            </Button>
          </Box>

          {/* Footer clarity */}
          <Typography
            sx={{
              mt: 2.5,
              fontSize: "0.75rem",
              textAlign: "center",
              color: "rgba(17,24,39,0.5)",
            }}
          >
            No setup. Just upload your sales data and see results instantly.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;